import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCheckmark1Small } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { selectArchitecture, usePositionBootstrap } from '../../../position-management';
import type { UrlTileSubject } from '../../../url-tiles';
import { aiTransformationRoute } from '../../route';
import type { TilePriority } from '../api/use-tile-priority';
import { useTilePriority } from '../api/use-tile-priority';
import type { PrioritizeScope } from '../model/prioritization';
import { groupScope, positionScope, scopeBaseFilters } from '../model/priority-scopes';
import { tilePriorityArchitectureIdOf, tilePriorityIdOf, tilePriorityKindOf } from '../model/tile-priority';
import type { StrategyViewState } from '../model/view-state';
import { DEFAULT_STRATEGY_VIEW_STATE, parseStrategyViewState, toQueryFiltersWithoutPick } from '../model/view-state';
import type { PriorityTarget } from '../priority-overrides';
import { usePrioritizedState } from '../priority-overrides';
import { isAiTransformationStrategyReleased } from '../strategy-release';
import { useStrategyPriority } from '../use-strategy-priority';

/** Both labels share one cell; the one going out slides up while the other slides in from below. */
const SLIDE_MS = 200;

const SLIDE =
    'col-start-1 row-start-1 w-max transition-[translate,opacity] duration-200 ease-standard motion-reduce:transition-none';

/** The width of the label shown, so the button fits it and eases to the other label's width when it changes. */
const useShownLabelWidth = (label: RefObject<HTMLSpanElement | null>): number | undefined => {
    const [width, setWidth] = useState<number>();

    useLayoutEffect(() => {
        const element = label.current;
        if (element === null) return;
        const measure = () => setWidth(element.offsetWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, [label]);

    return width;
};

const ALL_FILTERS = toQueryFiltersWithoutPick(DEFAULT_STRATEGY_VIEW_STATE);

/** What prioritizing a position, role or level takes along, as its row on the dashboard does. */
const scopeOf = (
    subject: UrlTileSubject,
    read: TilePriority,
    state: StrategyViewState,
): PrioritizeScope | undefined => {
    if (subject.kind === 'task') return undefined;
    if (subject.kind === 'position') return read.item === undefined ? undefined : positionScope(state, read.item);

    return groupScope(state, subject.kind === 'job-role' ? 'ROLE' : 'LEVEL', {
        id: subject.profileId,
        title: subject.title,
    });
};

const LoadedTilePriorityButton: FunctionComponent<{
    readonly architectureId: string;
    readonly read: TilePriority;
    readonly state: StrategyViewState;
    readonly subject: UrlTileSubject;
    readonly target: PriorityTarget;
}> = ({ architectureId, read, state, subject, target }) => {
    const translate = useTranslate();
    const priority = useStrategyPriority(architectureId);
    const isPrioritized = usePrioritizedState(target, read.isPrioritized);

    // The change shows at once, but is saved only once its slide is over: the refresh after a save stalls the page
    // for a moment, which would otherwise make the slide jump back and play again.
    const [pending, setPending] = useState<boolean>();
    const shown = pending ?? isPrioritized;

    const save = async (next: boolean): Promise<unknown> => {
        if (subject.kind === 'task') {
            return priority.moveItem(
                { id: subject.taskId, subject: 'TASK' },
                next ? 'PRIORITIZED' : 'IDLE',
                ALL_FILTERS,
                target,
            );
        }
        const scope = scopeOf(subject, read, state);
        if (scope === undefined) return undefined;

        return next ? priority.prioritize(scope, target) : priority.unprioritize(scope, target);
    };

    // A tile closed during the slide still saves the change, at once instead of after the slide.
    const slideRef = useRef<{ readonly run: () => void; readonly timer: number }>(undefined);
    useEffect(
        () => () => {
            const slide = slideRef.current;
            if (slide === undefined) return;
            window.clearTimeout(slide.timer);
            slide.run();
        },
        [],
    );

    const change = () => {
        if (pending !== undefined) return;
        const next = !shown;
        setPending(next);
        const run = () => {
            slideRef.current = undefined;
            void save(next).finally(() => setPending(undefined));
        };
        slideRef.current = { run, timer: window.setTimeout(run, SLIDE_MS) };
    };

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const addLabelRef = useRef<HTMLSpanElement>(null);
    const priorityLabelRef = useRef<HTMLSpanElement>(null);
    const labelWidth = useShownLabelWidth(shown ? priorityLabelRef : addLabelRef);
    const removeLabel = translate('pages.ai-transformation.strategy.priority.remove-from-priorities');

    return (
        <Tooltip open={isTooltipOpen && shown && read.canChange} onOpenChange={setIsTooltipOpen}>
            <Tooltip.Trigger asChild={true}>
                <Button
                    aria-pressed={shown}
                    className={clsx(shown && 'bg-transparent! shadow-none! hover:shadow-none!')}
                    data-prioritized={shown}
                    data-testid="tile-priority-button"
                    disabled={!read.canChange}
                    size="sm"
                    variant="secondary"
                    onClick={change}
                >
                    <span
                        className="grid overflow-hidden transition-[width] duration-200 ease-standard motion-reduce:transition-none"
                        style={{ width: labelWidth }}
                    >
                        <span
                            ref={addLabelRef}
                            aria-hidden={shown}
                            className={clsx(SLIDE, shown && '-translate-y-full opacity-0')}
                        >
                            {translate('pages.ai-transformation.strategy.priority.add-to-priorities')}
                        </span>
                        <span
                            ref={priorityLabelRef}
                            aria-hidden={!shown}
                            className={clsx(
                                SLIDE,
                                'flex items-center gap-xs text-text-success',
                                !shown && 'translate-y-full opacity-0',
                            )}
                        >
                            <IconCheckmark1Small aria-hidden={true} className="size-[14px] shrink-0" />
                            {translate('pages.ai-transformation.strategy.priority.priority')}
                        </span>
                    </span>
                </Button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content align="end" side="bottom" sideOffset={8}>
                    {removeLabel}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};

const ResolvedTilePriorityButton: FunctionComponent<{ readonly subject: UrlTileSubject }> = ({ subject }) => {
    const { pathname, search } = useLocation();
    const bootstrap = usePositionBootstrap();
    const isOnDashboard = pathname === aiTransformationRoute.patterns.aiTransformation;
    // On the dashboard the tile prioritizes as the page does, under its filters; elsewhere across the architecture.
    const state = useMemo(
        () => (isOnDashboard ? parseStrategyViewState(new URLSearchParams(search)) : DEFAULT_STRATEGY_VIEW_STATE),
        [isOnDashboard, search],
    );
    const architectures = bootstrap.data?.architectures;
    const architectureId = useMemo(() => {
        if (architectures === undefined) return undefined;
        const dashboardArchitectureId = isOnDashboard
            ? selectArchitecture(architectures, state.architectureId ?? null)?.id
            : undefined;

        return tilePriorityArchitectureIdOf(subject, architectures, dashboardArchitectureId);
    }, [architectures, isOnDashboard, state.architectureId, subject]);
    const kind = tilePriorityKindOf(subject);
    const id = tilePriorityIdOf(subject);
    const query = useTilePriority({
        architectureId,
        filters: kind === 'ROLE' || kind === 'LEVEL' ? scopeBaseFilters(state) : ALL_FILTERS,
        id,
        kind,
    });

    if (architectureId === undefined || is.nullOrUndefined(query.data)) return null;

    return (
        <LoadedTilePriorityButton
            architectureId={architectureId}
            read={query.data}
            state={state}
            subject={subject}
            target={{ architectureId, id, kind }}
        />
    );
};

/**
 * Adds a tile's position, task, role or level to the AI transformation priorities and takes it out again, as its row
 * on the dashboard does, shown only to those who may change its priority. A change shows at once, here and on the
 * dashboard, and is taken back when it fails.
 */
export const TilePriorityButton: FunctionComponent<{ readonly subject: UrlTileSubject }> = ({ subject }) => {
    const {
        applicationState: { featureFlagService },
    } = useApplicationContext();

    return isAiTransformationStrategyReleased(featureFlagService.isFeatureFlagEnabled) ? (
        <ResolvedTilePriorityButton subject={subject} />
    ) : null;
};
